/**
 * Permission catalog — FE mirror of the BE source of truth at
 * `dahling/api/src/common/permissions/permissions.ts`.
 *
 * Permissions are opaque strings the BE attaches to the user via /auth/signin
 * and /auth/me. The FE compares against this list via `usePermissions()`.
 *
 * Drift policy: BE owns the catalog. If BE renames a permission, components
 * gating on the old string fall through (return false → safe degradation).
 * Keep this file in sync when adding new gates.
 */
export const Permissions = {
  // §2 Projects
  PROJECT_VIEW: "project:view",
  PROJECT_CREATE: "project:create",
  PROJECT_EDIT: "project:edit",
  PROJECT_DELETE: "project:delete",
  PROJECT_ARCHIVE: "project:archive",

  // §3 Candidates
  CANDIDATE_VIEW: "candidate:view",
  CANDIDATE_SEARCH: "candidate:search",
  CANDIDATE_INGEST_MANUAL: "candidate:ingest_manual",
  CANDIDATE_EDIT_OWN_INGESTED: "candidate:edit_own_ingested",
  CANDIDATE_EDIT_ANY: "candidate:edit_any",
  CANDIDATE_DELETE: "candidate:delete",
  CANDIDATE_STATUS_UPDATE: "candidate:status_update",

  // §4 Talent pools
  TALENT_POOL_VIEW: "talent_pool:view",
  TALENT_POOL_EDIT: "talent_pool:edit",
  TALENT_POOL_CREATE: "talent_pool:create",
  TALENT_POOL_DELETE: "talent_pool:delete",

  // §5 Clients & companies
  CLIENT_VIEW: "client:view",
  CLIENT_CREATE: "client:create",
  CLIENT_EDIT: "client:edit",
  CLIENT_DELETE: "client:delete",
  COMPANY_VIEW: "company:view",
  COMPANY_EDIT: "company:edit",

  // §7 AI
  AI_CHAT: "ai:chat",
  AI_GENERATE_QUESTIONS: "ai:generate_questions",
  AI_PARSE_DOCUMENTS: "ai:parse_documents",
  AI_INDEX_CANDIDATE: "ai:index_candidate",
  AI_SET_CANDIDATE_ACTIVE: "ai:set_candidate_active",
  AI_SCORE_INTERVIEW: "ai:score_interview",
  AI_GENERATE_EMAIL: "ai:generate_email",
  AI_SUMMARY: "ai:summary",

  // §8 LinkedIn
  LINKEDIN_SEARCH: "linkedin:search",
  LINKEDIN_SEND_INMAIL: "linkedin:send_inmail",

  // §9 Admin
  SALESFORCE_ACCESS: "salesforce:access",
  AUDIT_LOG_VIEW: "audit:view",
  SAGEMAKER_INVOKE: "sagemaker:invoke",
  SAGEMAKER_STATUS: "sagemaker:status",
  SEARCH_TRACE_VIEW: "search:trace:view",

  // §11 User Management (SA only)
  USER_VIEW: "user:view",
  USER_CREATE: "user:create",
  USER_UPDATE: "user:update",

  // §10 Feedback
  FEEDBACK_SUBMIT: "feedback:submit",
  FEEDBACK_ADMIN_VIEW: "feedback:admin_view",
  FEEDBACK_ADMIN_MANAGE: "feedback:admin_manage",

  // §13 Analytics & Reporting
  ANALYTICS_VIEW_ALL: "analytics:view_all",
  ANALYTICS_VIEW_OWN: "analytics:view_own",
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];
